import { useEffect, useState } from 'react';
import { Card, Form, Button, Alert, Row, Col } from 'react-bootstrap';
import type { components } from '@telemedicine/shared';
import {
  assignCase,
  unassignCase,
  changeCaseSpecialty,
  changeCasePriority,
  listReviewers,
} from '../services/api';
import { SpecialtySelect } from '../components/SpecialtySelect';
import { BusyButton } from '../components/BusyButton';
import { useConfirm } from '../components/ConfirmProvider';
import { useToast } from '../components/ToastProvider';
import { errMessage } from '../errors';
import { enumLabel } from '../labels';
import { getLang } from '../language';

type Case = components['schemas']['Case'];
type Reviewer = components['schemas']['Reviewer'];
type Priority = components['schemas']['Priority'];

const PRIORITIES: Priority[] = ['LOW', 'MEDIUM', 'HIGH', 'URGENT'];

// Management actions apply while a case is active (not draft, not closed).
const ACTIVE_STATUSES = [
  'SUBMITTED',
  'TRIAGE',
  'ASSIGNED',
  'UNDER_REVIEW',
  'WAITING_FOR_LOCAL_DOCTOR',
  'VIDEO_REQUIRED',
  'SPECIALIST_FACE_TO_FACE',
  'RECOMMENDATION_PROVIDED',
  'LOCAL_FOLLOW_UP',
];

/**
 * Case management controls (distinct from the workflow "next step" in CaseActions):
 *  - Reassign reviewer — local doctor (own case) + admins.
 *  - Release to triage (unassign) — local doctor + admins, and the assigned reviewer as "decline".
 *  - Change specialty — local doctor + admins (a reviewer releases to triage instead).
 *  - Change priority (clinical urgency) — local doctor, reviewer, and admins.
 * Shown only while the case is active. Each action confirms where destructive and toasts on done.
 */
export function CaseManage({
  theCase,
  roles,
  onChanged,
  onReleased,
}: {
  theCase: Case;
  roles: string[];
  onChanged: () => void;
  /** Called after release-to-triage: the actor may lose access, so the parent navigates away. */
  onReleased?: () => void;
}) {
  const lang = getLang();
  const [reviewers, setReviewers] = useState<Reviewer[]>([]);
  const [reviewerId, setReviewerId] = useState('');
  const [specialtyId, setSpecialtyId] = useState(theCase.specialtyId ?? '');
  const [priority, setPriority] = useState<Priority>(theCase.priority);
  // Which action is currently running, so only that button shows a spinner (they share one
  // handler but should give independent feedback). null = idle.
  const [runningAction, setRunningAction] = useState<
    'priority' | 'specialty' | 'reassign' | 'release' | null
  >(null);
  const busy = runningAction !== null;
  const [error, setError] = useState<string | null>(null);
  const confirm = useConfirm();
  const toast = useToast();

  const isAdmin = roles.includes('ADMIN') || roles.includes('GLOBAL_ADMIN');
  const isLocal = roles.includes('LOCAL_DOCTOR');
  const isReviewer = roles.includes('REVIEWER');
  const active = ACTIVE_STATUSES.includes(theCase.status);
  const assigned = !!theCase.assignedTo;

  // Who can do what (server enforces too).
  const canReassign = active && (isLocal || isAdmin);
  const canChangeSpecialty = active && (isLocal || isAdmin);
  const canRelease = active && assigned && (isLocal || isAdmin || isReviewer);
  const canChangePriority = active; // local doctor, reviewer, or admin — all clinician roles

  useEffect(() => {
    setSpecialtyId(theCase.specialtyId ?? '');
    setPriority(theCase.priority);
  }, [theCase.specialtyId, theCase.priority]);

  useEffect(() => {
    if (canReassign) {
      listReviewers().then((r) => setReviewers(r.items)).catch(() => setReviewers([]));
    }
  }, [canReassign]);

  if (!canReassign && !canChangeSpecialty && !canRelease && !canChangePriority) return null;

  const run = async (
    action: 'priority' | 'specialty' | 'reassign' | 'release',
    fn: () => Promise<unknown>,
    opts: { confirm?: Parameters<typeof confirm>[0]; success: string; after?: () => void },
  ) => {
    if (opts.confirm && !(await confirm(opts.confirm))) return;
    setRunningAction(action);
    setError(null);
    try {
      await fn();
      toast.success(opts.success);
      // Default is to refresh the current case; a release uses `after` to navigate away instead,
      // because the actor (a reviewer) may lose read access once the case leaves them.
      (opts.after ?? onChanged)();
    } catch (e) {
      const msg = errMessage(e, 'Action failed');
      setError(msg);
      toast.error(msg);
    } finally {
      setRunningAction(null);
    }
  };

  const midReview = ['UNDER_REVIEW', 'VIDEO_REQUIRED', 'SPECIALIST_FACE_TO_FACE'].includes(
    theCase.status,
  );

  return (
    <Card className="shadow-sm mb-3">
      <Card.Header>Manage case</Card.Header>
      <Card.Body>
        {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

        {/* Priority — any engaged clinician. */}
        {canChangePriority && (
          <Row className="g-2 align-items-end mb-3">
            <Col sm={7}>
              <Form.Label className="small text-muted">Priority (clinical urgency)</Form.Label>
              <Form.Select value={priority} onChange={(e) => setPriority(e.target.value as Priority)}>
                {PRIORITIES.map((p) => (
                  <option key={p} value={p}>{enumLabel('priority', p, lang)}</option>
                ))}
              </Form.Select>
            </Col>
            <Col sm={5}>
              <BusyButton
                variant="outline-primary"
                className="w-100"
                loading={runningAction === 'priority'}
                busyLabel="Updating…"
                disabled={busy || priority === theCase.priority}
                onClick={() =>
                  run('priority', () => changeCasePriority(theCase.caseId, { version: theCase.version, priority }), {
                    success: 'Priority updated.',
                  })
                }
              >
                Update priority
              </BusyButton>
            </Col>
          </Row>
        )}

        {/* Specialty — local doctor + admins. */}
        {canChangeSpecialty && (
          <div className="mb-3">
            <Form.Label className="small text-muted">Specialty</Form.Label>
            <SpecialtySelect value={specialtyId} onChange={setSpecialtyId} allowEmpty={false} />
            <BusyButton
              variant="outline-primary"
              size="sm"
              className="mt-2"
              loading={runningAction === 'specialty'}
              busyLabel="Updating…"
              disabled={busy || !specialtyId || specialtyId === theCase.specialtyId}
              onClick={() =>
                run('specialty', () => changeCaseSpecialty(theCase.caseId, { version: theCase.version, specialtyId }), {
                  success: 'Specialty updated.',
                })
              }
            >
              Update specialty
            </BusyButton>
          </div>
        )}

        {/* Reassign reviewer — local doctor + admins. */}
        {canReassign && (
          <Row className="g-2 align-items-end mb-3">
            <Col sm={7}>
              <Form.Label className="small text-muted">
                {assigned ? 'Reassign to reviewer' : 'Assign reviewer'}
              </Form.Label>
              <Form.Select value={reviewerId} onChange={(e) => setReviewerId(e.target.value)}>
                <option value="">Select a reviewer…</option>
                {reviewers.map((r) => (
                  <option key={r.userId} value={r.userId}>{r.name ?? r.email}</option>
                ))}
              </Form.Select>
            </Col>
            <Col sm={5}>
              <BusyButton
                variant="outline-primary"
                className="w-100"
                loading={runningAction === 'reassign'}
                busyLabel={assigned ? 'Reassigning…' : 'Assigning…'}
                disabled={busy || !reviewerId || reviewerId === theCase.assignedTo}
                onClick={() =>
                  run(
                    'reassign',
                    () =>
                      assignCase(theCase.caseId, {
                        version: theCase.version,
                        reviewerId,
                        specialtyId: specialtyId || null,
                      }),
                    {
                      success: 'Reviewer reassigned.',
                      confirm: midReview
                        ? {
                            title: 'Reassign mid-review?',
                            body: 'This case is being reviewed. Reassigning moves it to another reviewer; the current reviewer will no longer see it as theirs. Continue?',
                            confirmLabel: 'Reassign',
                            variant: 'warning',
                          }
                        : undefined,
                    },
                  )
                }
              >
                {assigned ? 'Reassign' : 'Assign'}
              </BusyButton>
            </Col>
          </Row>
        )}

        {/* Release to triage — local doctor + admins, and the assigned reviewer as decline. */}
        {canRelease && (
          <BusyButton
            variant="outline-danger"
            size="sm"
            loading={runningAction === 'release'}
            busyLabel="Releasing…"
            disabled={busy}
            onClick={() =>
              run('release', () => unassignCase(theCase.caseId, { version: theCase.version }), {
                success: 'Case released to triage.',
                // Navigate back to My cases: a reviewer loses read access once it's unassigned,
                // and any actor is signalling they're done with it.
                after: onReleased,
                confirm: {
                  title: isReviewer && !isAdmin && !isLocal ? 'Decline this case?' : 'Release to triage?',
                  body:
                    isReviewer && !isAdmin && !isLocal
                      ? 'This returns the case to triage so it can be assigned to another reviewer. Continue?'
                      : 'This unassigns the reviewer and returns the case to triage for re-routing. Continue?',
                  confirmLabel: 'Release',
                  variant: 'danger',
                },
              })
            }
          >
            {isReviewer && !isAdmin && !isLocal ? 'Decline (release to triage)' : 'Release to triage'}
          </BusyButton>
        )}
      </Card.Body>
    </Card>
  );
}
